/**
 * 危化品事故场景中心 — 公开资料研究参考的内容层。
 *
 * 这些内容来自用户提供的 SC-CHEMICAL-01 执行包（02_CONTENT/chemical-incident-content.json
 * 与 03_RESEARCH），在本页中以业务阅读语言呈现。它只回答四个问题：事故常见的关键问题、
 * 需要掌握的信息、哪些技术能力可以帮助获取信息，以及哪些官方资料支撑这些说法。
 *
 * 这一层不是：
 *   不是正式 Capability 定义或 Capability ID；不是设备清单或配备状态；
 *   不是属地预案、现场事实或行动授权；不创建 Mission、Scenario Pin 或版本。
 *
 * 安全边界（执行包 03_RESEARCH/safety-and-authority-boundary.md）：
 *   不提供中和、堵漏、封堵、转移、灭火介质选择或工艺操作步骤；
 *   不生成警戒半径、疏散距离、扩散距离、爆炸影响距离或任何可被误用为行动阈值的数值；
 *   不据单张图片或识别结果确认现场化学品身份；不把单一读数等同于整个区域安全。
 */

import { chemicalInformationDetails } from './chemical-information-details';
import { informationDetailKey, type ResearchInformationDetail } from './research-information';

/** 研究内容的性质标签。与森林场景使用同一套“公开研究参考”语义。 */
export const chemicalReferenceNature = 'PUBLIC_RESEARCH_REFERENCE_PENDING_SCENARIO_OWNER';

/** 页面固定提示。执行包要求逐字保留。 */
export const chemicalSafetyNotice =
  '本内容为公开资料研究参考，不代表属地预案、现场事实或行动授权。实际事故处置应由属地专业指挥体系依据现场证据、适用法规和专业人员判断组织实施。';

/** 三条资料分类，按阅读顺序排列。 */
export type ChemicalEvidenceGroup = '法规与标准' | '事故调查报告' | '官方演练与实践';

export const chemicalEvidenceGroupOrder: readonly ChemicalEvidenceGroup[] = [
  '法规与标准',
  '事故调查报告',
  '官方演练与实践',
];

export interface ChemicalSource {
  readonly id: string;
  readonly title: string;
  readonly publisher: string;
  readonly year: string;
  readonly kind: string;
  readonly group: ChemicalEvidenceGroup;
  readonly url: string;
  readonly summary: string;
  /** 这份资料的适用边界或状态说明。必须与摘要分开显示。 */
  readonly caveat: string;
}

const sources: readonly ChemicalSource[] = [
  {
    id: 'SRC-REG-591',
    title: '危险化学品安全管理条例（国务院令第591号）',
    publisher: '应急管理部 / 中国政府网',
    year: '2011',
    kind: '行政法规',
    group: '法规与标准',
    url: 'https://www.mem.gov.cn/gk/gwgg/xgxywj/wxhxp_228/201103/t20110312_232614.shtml',
    summary:
      '第六十六至七十四条涉及危险化学品登记信息、事故应急预案、营救疏散、危险源控制、性质及危害区域测定、封闭隔离洗消和环境监测评估。',
    caveat: '官方页面为2011年修订发布；正式引用前应核验最新修订状态。',
  },
  {
    id: 'SRC-REG-708',
    title: '生产安全事故应急条例（国务院令第708号）',
    publisher: '应急管理部 / 国务院办公厅',
    year: '2019',
    kind: '行政法规',
    group: '法规与标准',
    url: 'https://www.mem.gov.cn/fw/flfgbz/fg/201903/t20190301_375884.shtml',
    summary:
      '用于支持风险辨识评估、应急预案、应急组织职责和应急准备等通用要求；页面列明自2019年4月1日起施行。',
    caveat: '通用应急要求，不针对具体化学品，也不替代属地预案。',
  },
  {
    id: 'SRC-AQ-3052',
    title: 'AQ/T 3052—2015 危险化学品事故应急救援指挥导则',
    publisher: '应急管理部',
    year: '2015',
    kind: '行业标准',
    group: '法规与标准',
    url: 'https://www.mem.gov.cn/gk/gwgg/agwzlfl/gg_01/201503/W020171101558709728116.pdf',
    summary:
      '规定危险化学品事故应急救援指挥的基本原则和程序，适用于政府部门、外部救援力量和事故单位共同参与救援的情形。',
    caveat: '官方发布资料，标准现行状态需以主管部门查询为准。',
  },
  {
    id: 'SRC-GBT-16483',
    title: 'GB/T 16483-2008 化学品安全技术说明书 内容和项目顺序',
    publisher: '国家标准全文公开系统',
    year: '2008',
    kind: '国家标准',
    group: '法规与标准',
    url: 'https://openstd.samr.gov.cn/bzgk/std/newGbInfo?hcno=67992CA972A4CF9222095CA06064724A',
    summary: '规定化学品安全技术说明书（SDS）的内容与项目顺序，可用于理解 SDS 应当包含哪些信息。',
    caveat:
      '国家标准官方页面当前显示“现行”；标准网页不是具体化学品的 SDS，也不能代替企业提供的版本。',
  },
  {
    id: 'SRC-GB-18218',
    title: 'GB 18218-2018 危险化学品重大危险源辨识',
    publisher: '国家标准全文公开系统',
    year: '2018',
    kind: '国家标准',
    group: '法规与标准',
    url: 'https://openstd.samr.gov.cn/bzgk/std/newGbInfo?hcno=E976F49B263D7579CCABD419B1E40225',
    summary: '用于理解重大危险源辨识的制度背景，以及企业为何应当保留物质与数量记录。',
    caveat: '国家标准官方页面当前显示“现行”；辨识制度不是现场事故处置步骤。',
  },
  {
    id: 'SRC-DRAFT-2026',
    title: '危险化学品事故应急救援规程（征求意见稿）公开征求意见通知',
    publisher: '国家安全生产应急救援中心 / 应急管理部',
    year: '2026',
    kind: '征求意见稿',
    group: '法规与标准',
    url: 'https://www.mem.gov.cn/gk/zfxxgkpt/fdzdgknr/202610/t20261012_735251.shtml',
    summary:
      '2026-10-12 发布。通知说明规程拟明确应急救援基本程序、现场处置方法、分类处置要点和现场急救。',
    caveat:
      '征求意见稿，仅供研究参考：征求意见期为 2026-10-13 至 2026-12-13，不得称为正式标准或现行规范。',
  },
  {
    id: 'SRC-COMMAND-QA',
    title: '关于企业专职消防队的应急处置指挥权问题（答复）',
    publisher: '应急管理部 / 国家安全生产应急救援中心',
    year: '2025',
    kind: '官方答复',
    group: '法规与标准',
    url: 'https://www.mem.gov.cn/hd/gzly/lyhf/202512/t20251225_589878.shtml',
    summary:
      '答复强调统一指挥、分级负责、流程规范、协同高效，并提及 AQ/T 3052—2015 与《典型危险化学品应急处置指导手册》。',
    caveat: '官方答复，用于理解指挥责任边界，不替代属地预案和具体授权。',
  },
];

export interface ChemicalCase {
  readonly id: string;
  readonly title: string;
  readonly publisher: string;
  readonly year: string;
  /** 资料类型标签，逐字标注资料来源的性质。 */
  readonly kind: string;
  readonly group: ChemicalEvidenceGroup;
  /**
   * 官方原文地址。为 null 表示来源登记表没有给出与该案例一一对应的官方页面：
   * 页面只写“待核实”，不借用另一份案例的链接冒充原文。
   */
  readonly url: string | null;
  readonly summary: string;
  readonly caveat: string;
}

const cases: readonly ChemicalCase[] = [
  {
    id: 'CASE-TIANJIN',
    title: '天津港“8·12”瑞海公司危险品仓库特别重大火灾爆炸事故调查报告',
    publisher: '应急管理部（国务院事故调查组报告公开版）',
    year: '2016',
    kind: '官方事故调查报告',
    group: '事故调查报告',
    url: 'https://www.mem.gov.cn/gk/sgcc/tbzdsgdcbg/2016/201602/P020190415543917598002.pdf',
    summary:
      '报告记录多种危险货物在同一事故中的燃烧、爆炸或泄漏扩散，并对大气、水体和土壤环境影响开展监测分析。',
    caveat: '事故发生于2015年，报告为历史案例；不构成本次现场的处置指导。',
  },
  {
    id: 'CASE-XIANGSHUI',
    title: '江苏响水天嘉宜化工有限公司“3·21”特别重大爆炸事故调查报告',
    publisher: '国务院事故调查组 / 应急管理部',
    year: '2019',
    kind: '官方事故调查报告',
    group: '事故调查报告',
    url: 'https://www.mem.gov.cn/gk/sgcc/tbzdsgdcbg/2019tbzdsgcc/201911/P020191115565111829069.pdf',
    summary: '报告记载，旧固废库内长期违法贮存的硝化废料持续积热升温并自燃，燃烧引发爆炸。',
    caveat: '2019年历史案例；说明风险可能在事故前长期积累，不代表当前现场状态。',
  },
  {
    id: 'CASE-ZHANGZHOU',
    title: '福建漳州腾龙芳烃“4·6”爆炸着火事故',
    publisher: '应急管理部',
    year: '2015',
    kind: '应急管理部事故摘要',
    group: '事故调查报告',
    url: 'https://www.mem.gov.cn/fw/jsxx/202104/t20210402_382678.shtml',
    summary:
      '公开资料记载，二甲苯装置开工引料过程中出现压力和流量波动，管道焊口断裂、物料外泄并引发爆炸着火。',
    caveat: '官方事故摘要，不是完整调查报告；只复述摘要支持的事实。',
  },
  {
    id: 'CASE-H2S',
    title: '硫化氢中毒与盲目施救事故案例',
    publisher: '应急管理部',
    year: '2016',
    kind: '事故警示资料',
    group: '事故调查报告',
    url: null,
    summary: '公开案例记载，硫化氢逸散导致人员中毒，未采取防护措施的盲目施救使事故后果扩大。',
    caveat:
      '事故警示摘要；用于说明人员暴露与救援人员安全信息的重要性。来源登记表未给出与该案例一一对应的官方页面，原文链接待核实。',
  },
  {
    id: 'CASE-YIWU-DRILL',
    title: '浙江义乌消防救援支队危险化学品灾害事故处置实战演练',
    publisher: '应急管理部 / 浙江省消防救援总队',
    year: '2023',
    kind: '官方演练案例（非真实事故）',
    group: '官方演练与实践',
    url: 'https://www.mem.gov.cn/xw/jyll/202304/t20230429_449335.shtml',
    summary:
      '官方报道介绍了桌面推演与实战演练，涉及现场侦察、警戒、人员疏散、安全防护、清理洗消、通信保障及多部门协同。',
    caveat: '演练案例，非真实事故；演练中的做法不构成实际处置结论。',
  },
];

export const chemicalSources = sources;
export const chemicalCases = cases;

export function findChemicalSource(id: string) {
  return sources.find((row) => row.id === id);
}

export function findChemicalCase(id: string) {
  return cases.find((row) => row.id === id);
}

/* ------------------------------------------------------------------ capabilities */

export interface ChemicalCapability {
  readonly key: string;
  readonly title: string;
  /** 它解决什么问题。 */
  readonly problem: string;
  /** 实际做什么。 */
  readonly role: string;
  readonly inputs: readonly string[];
  readonly outputs: readonly string[];
  /** 适用条件与限制。 */
  readonly limits: string;
  /** 资产文件名，位于 ui/assets/chemical/。 */
  readonly image: string;
  readonly sourceIds: readonly string[];
  readonly caseIds: readonly string[];
}

const capabilities: readonly ChemicalCapability[] = [
  {
    key: 'chemical-document',
    title: '化学品信息识别与SDS核验',
    problem: '现场标签、运单、企业台账与安全技术说明书常常残缺、版本不一或彼此矛盾。',
    role: '把标签、运输单证、企业台账和 SDS 交叉核对，形成物质身份候选、来源和资料缺口。',
    inputs: ['容器与运输标识', 'SDS及版本信息', '企业台账与库存记录', '企业技术人员提供的资料'],
    outputs: ['物质身份候选及来源', '危险特性资料索引', '资料冲突与缺失清单'],
    limits: '图像识别只能辅助读取，不能单独确认实际装载物；名称相似不等于同一物质。',
    image: '01-chemical-identity-sds.png',
    sourceIds: ['SRC-GBT-16483', 'SRC-REG-591'],
    caseIds: ['CASE-XIANGSHUI'],
  },
  {
    key: 'gas-detection',
    title: '多气体与环境参数检测',
    problem: '同一区域的不同位置、不同时间，气体与环境参数可能差异很大。',
    role: '用固定或移动设备记录不同地点、不同时间的气体及环境参数。',
    inputs: ['检测设备读数', '设备类型与校准状态', '检测点位和时间', '现场环境与气象记录'],
    outputs: ['带时间和位置的检测记录', '变化趋势与异常提示', '待复核的数据点'],
    limits:
      '受传感器类型、量程、校准和采样位置影响；单一读数不代表整个区域安全，也不能作为通用阈值。',
    image: '02-gas-detection.png',
    sourceIds: ['SRC-GB-18218', 'SRC-DRAFT-2026'],
    caseIds: [],
  },
  {
    key: 'remote-recon',
    title: '远程侦察与危险源观察',
    problem: '部分区域不宜直接靠近，但需要持续了解设备、容器和管线外观的变化。',
    role: '用远程摄像、无人机或地面机器人获取不宜直接靠近区域的观察资料。',
    inputs: ['可见光图像与视频', '热成像或其他传感数据（如设备具备）', '设备位置、时间与运行状态'],
    outputs: ['远程观察画面', '设备外观和现场变化记录', '需要专业复核的位置线索'],
    limits: '图像不能证明化学品种类、浓度或区域安全；设备适用性须核验。',
    image: '03-remote-robot-recon.png',
    sourceIds: ['SRC-AQ-3052'],
    caseIds: ['CASE-TIANJIN'],
  },
  {
    key: 'fire-observation',
    title: '火情与热异常观察',
    problem: '泄漏、受热或反应可能使火情与烟气的形态快速变化，需要连续记录。',
    role: '整合视频、热成像和设备状态，持续观察火情、烟气和热异常变化。',
    inputs: ['现场视频与图像', '热成像记录（如有）', '设备仪表和报警记录', '现场观察时间线'],
    outputs: ['火情变化记录', '热异常观察资料', '多源信息对照'],
    limits: '不能据此自动选择灭火介质、冷却方式或处置动作。',
    image: '04-fire-thermal-observation.png',
    sourceIds: ['SRC-AQ-3052'],
    caseIds: ['CASE-ZHANGZHOU'],
  },
  {
    key: 'protection-decon',
    title: '人员防护、洗消与医疗交接',
    problem: '人员身份、位置、接触时间和防护状态容易分散在不同部门，交接时缺失。',
    role: '展示专业防护资源、人员防护状态、洗消区域和医疗交接信息。',
    inputs: ['人员状态记录', '防护装备与人员配置记录', '洗消区域状态', '医疗交接信息'],
    outputs: ['人员状态清单', '洗消与医疗交接记录', '待确认人员信息'],
    limits: '不生成防护等级、洗消配方或医疗诊断；由专业人员按属地预案确认。',
    image: '05-protection-decontamination.png',
    sourceIds: ['SRC-REG-591', 'SRC-REG-708'],
    caseIds: ['CASE-H2S', 'CASE-YIWU-DRILL'],
  },
  {
    key: 'environment-sampling',
    title: '环境监测与采样',
    problem: '事故控制不等于环境影响结束，空气、水体、土壤和径流需要分别记录。',
    role: '展示空气、水体、土壤和地表径流的监测点位、采样与检测记录。',
    inputs: ['监测点位', '采样时间与样品编号', '检测机构与检测结果', '排水和径流资料'],
    outputs: ['环境监测记录', '样品与检测结果索引', '连续监测和待补采信息'],
    limits: '示意图不代表真实检测；环境结论由有资质部门或机构负责。',
    image: '06-environment-sampling.png',
    sourceIds: ['SRC-REG-591'],
    caseIds: ['CASE-TIANJIN'],
  },
  {
    key: 'command-coordination',
    title: '现场通信与协同指挥',
    problem: '企业、消防、应急、医疗、公安和生态环境的报告往往口径、时间与位置不一致。',
    role: '把企业、消防、应急、医疗、公安和生态环境等信息按来源、时间和位置组织起来。',
    inputs: ['部门和队伍报告', '现场视频与监测摘要', '人员和资源状态', '时间线与位置记录'],
    outputs: ['协同态势摘要', '来源可追溯的信息时间线', '待确认事项与交接记录'],
    limits: '只做信息汇集展示，不替代法定指挥体系，不自动下达行动命令。',
    image: '07-command-coordination.png',
    sourceIds: ['SRC-AQ-3052', 'SRC-COMMAND-QA'],
    caseIds: ['CASE-YIWU-DRILL'],
  },
];

/**
 * 七项能力研究主题。
 *
 * `formalCapabilityId` 恒为 null：这些是编辑层研究主题，不是正式 Capability ID，
 * 也不代表能力已接入、已验证、已配备或已获授权。
 */
export const chemicalCapabilities: readonly (ChemicalCapability & {
  readonly formalCapabilityId: null;
})[] = capabilities.map((row) => ({ ...row, formalCapabilityId: null }));

export function findChemicalCapability(key: string) {
  return capabilities.find((row) => row.key === key);
}

/* ------------------------------------------------------------------ typical issues */

/**
 * 一个问题里的一项能力：只写这项能力在这个问题中做什么、能提供什么。
 * 同一项能力在不同问题下的用途必须不同，不能共用一句通用说明。
 */
export interface ChemicalIssueCapability {
  readonly key: string;
  readonly purpose: string;
  readonly output: string;
}

export interface ChemicalInformationNeed {
  readonly name: string;
  readonly purpose: string;
  readonly acquisition: string;
  readonly sourceIds: readonly string[];
  /** 逐项详解：为什么需要、需要拿到什么、缺失会怎样、能说明与不能说明什么。 */
  readonly detail?: ResearchInformationDetail | undefined;
}

export interface ChemicalIssue {
  readonly key: string;
  readonly title: string;
  readonly summary: string;
  readonly whyItMatters: string;
  /** 目前无法确认的部分。页面统一显示为“待核实”。 */
  readonly unknowns: string;
  readonly information: readonly ChemicalInformationNeed[];
  readonly capabilities: readonly ChemicalIssueCapability[];
  readonly sourceIds: readonly string[];
  readonly caseIds: readonly string[];
}

const issues: readonly ChemicalIssue[] = [
  {
    key: 'chemical-identity',
    title: '化学品身份与危险特性不明',
    summary: '标签、运输标识、企业台账和安全技术说明书（SDS）可能缺失或不一致。',
    whyItMatters:
      '物质身份不清，会影响对毒害、腐蚀、燃烧、爆炸、反应性及环境危害的理解。不能仅凭颜色、气味、容器外观或单一标签作出确认。',
    unknowns: '标签是否与实际装载物一致；是否有混装、残留物或未申报物质。',
    information: [
      {
        name: '物质名称与识别编号',
        purpose: '把现场看到的容器、标识对应到一条可查的物质记录',
        acquisition: '危险货物标签、运输单证、企业台账',
        sourceIds: ['SRC-REG-591'],
      },
      {
        name: '危险标签与运输单证',
        purpose: '核对托运申报与实际装载是否一致',
        acquisition: '容器标识、运输单证、押运人员说明',
        sourceIds: ['SRC-REG-591'],
      },
      {
        name: 'SDS 版本与内容',
        purpose: '了解危险特性、相容性与防护提示，并记录版本',
        acquisition: '企业提供的安全技术说明书，需注明版本号',
        sourceIds: ['SRC-GBT-16483'],
      },
      {
        name: '混合物、浓度、物理状态与数量',
        purpose: '理解危害程度与可能的释放行为',
        acquisition: '企业台账、库存记录、生产工艺资料',
        sourceIds: ['SRC-GB-18218'],
      },
      {
        name: '混装、残留物与未申报物质线索',
        purpose: '识别身份类信息中最大的缺口',
        acquisition: '企业技术人员说明、历史台账与运输记录',
        sourceIds: ['SRC-REG-591'],
      },
    ],
    capabilities: [
      {
        key: 'chemical-document',
        purpose:
          '把标签、运输单证、企业台账与 SDS 版本放在一起比对，列出候选物质身份和相互矛盾之处。',
        output: '物质身份候选、危险特性资料索引，以及需要企业确认的差异清单。',
      },
      {
        key: 'gas-detection',
        purpose: '在不接触容器的情况下记录不同位置的气体和环境参数，作为身份判定的辅助线索之一。',
        output: '带点位和时间的检测记录；只能支持或质疑某个候选，不单独确认物质。',
      },
    ],
    sourceIds: ['SRC-REG-591', 'SRC-GBT-16483', 'SRC-GB-18218'],
    caseIds: ['CASE-TIANJIN', 'CASE-XIANGSHUI'],
  },
  {
    key: 'release-source',
    title: '泄漏源与释放状态不清',
    summary: '需要识别涉及的设备、容器、管线或运输载体，以及泄漏现象随时间的变化。',
    whyItMatters:
      '释放源、介质状态和变化过程不清，会使事故态势判断缺少关键依据。平台应展示来源、观测时间和待核实项，而不是自行推断泄漏量或危险边界。',
    unknowns: '释放是否仍在持续；源头是否经企业技术人员或专业队伍核实。',
    information: [
      {
        name: '释放位置与载体',
        purpose: '明确涉及的是设备、容器、管线还是运输车辆',
        acquisition: '企业工艺与设备资料、现场报告',
        sourceIds: ['SRC-REG-591'],
      },
      {
        name: '首次发现时间与报告来源',
        purpose: '建立可追溯的时间起点',
        acquisition: '报警记录、值班记录、报告人说明',
        sourceIds: ['SRC-AQ-3052'],
      },
      {
        name: '释放现象的持续变化',
        purpose: '判断释放是否仍在持续，变化是否有记录',
        acquisition: '现场观察记录、视频与监测记录',
        sourceIds: ['SRC-AQ-3052'],
      },
      {
        name: '固定与移动监测记录',
        purpose: '用带点位和时间的读数支持或质疑当前判断',
        acquisition: '企业固定监测、救援队伍移动检测',
        sourceIds: ['SRC-AQ-3052'],
      },
      {
        name: '气象记录',
        purpose: '理解释放物可能的移动方向与稀释条件',
        acquisition: '属地气象数据、现场气象观测',
        sourceIds: ['SRC-DRAFT-2026'],
      },
    ],
    capabilities: [
      {
        key: 'gas-detection',
        purpose: '沿可能的释放路径布点记录读数变化，帮助判断释放是否仍在持续。',
        output: '按点位、时间排列的浓度与环境参数记录，以及需要复核的异常点。',
      },
      {
        key: 'remote-recon',
        purpose: '在不宜直接靠近的区域内观察设备、容器和管线外观，寻找可见的泄漏迹象。',
        output: '远程画面与设备外观变化记录，并标出需要专业复核的位置。',
      },
      {
        key: 'command-coordination',
        purpose: '把企业技术人员、现场队伍和监测点的报告放在同一条时间线上。',
        output: '来源可追溯的释放情况时间线，以及仍待核实的环节。',
      },
    ],
    sourceIds: ['SRC-REG-591', 'SRC-AQ-3052', 'SRC-DRAFT-2026'],
    caseIds: ['CASE-ZHANGZHOU', 'CASE-TIANJIN'],
  },
  {
    key: 'human-exposure',
    title: '人员暴露与安全状态不明',
    summary: '需要掌握受影响人员、作业人员、周边群众和救援人员的数量、位置、接触时间及状态。',
    whyItMatters:
      '人员位置和暴露信息不完整，会影响医疗救治衔接与救援人员安全管理。应突出人员状态和专业交接信息，而不是由平台判断医疗结论。',
    unknowns: '是否仍有人员处于未确认区域；是否存在未登记承包商或访客。',
    information: [
      {
        name: '人员数量与位置',
        purpose: '确定有哪些人可能受影响，以及他们在哪里',
        acquisition: '企业考勤与访客登记、部门清点记录',
        sourceIds: ['SRC-REG-708'],
      },
      {
        name: '最后出现时间与暴露时间线',
        purpose: '为医疗交接提供时间依据',
        acquisition: '现场报告、视频时间戳、人员说明',
        sourceIds: ['SRC-AQ-3052'],
      },
      {
        name: '专业医疗记录',
        purpose: '记录专业医疗判断和救治去向',
        acquisition: '急救与接诊医疗机构',
        sourceIds: ['SRC-REG-708'],
      },
      {
        name: '防护状态与防护装备记录',
        purpose: '区分已防护与未防护人员，避免把两者混为一起',
        acquisition: '队伍装备记录、现场指挥记录',
        sourceIds: ['SRC-DRAFT-2026'],
      },
      {
        name: '洗消与医疗交接记录',
        purpose: '保留从现场到医疗机构的交接链条',
        acquisition: '洗消点记录、交接单',
        sourceIds: ['SRC-REG-591'],
      },
    ],
    capabilities: [
      {
        key: 'gas-detection',
        purpose: '记录人员活动区域周边的气体读数，作为是否需要撤离和防护的参考之一。',
        output: '人员所在区域附近的检测记录；单点读数不能代表整个区域安全。',
      },
      {
        key: 'protection-decon',
        purpose: '汇总人员身份、位置、接触时间，以及防护、洗消和医疗交接记录。',
        output: '人员状态清单与交接记录，并标出尚未确认的人员。',
      },
      {
        key: 'command-coordination',
        purpose: '把公安、医疗、企业和社会救援力量的报告汇集到同一时间线，减少重复核实。',
        output: '协同态势摘要与待确认事项清单。',
      },
    ],
    sourceIds: ['SRC-REG-708', 'SRC-AQ-3052', 'SRC-DRAFT-2026'],
    caseIds: ['CASE-H2S', 'CASE-YIWU-DRILL'],
  },
  {
    key: 'fire-explosion',
    title: '火灾、爆炸与反应风险',
    summary: '泄漏、受热、压力异常或物质相互作用可能引发火灾、爆炸或进一步反应。',
    whyItMatters:
      '事故可能从单一泄漏演变为火灾、爆炸、容器破坏或多物质连锁影响。平台应呈现已知物质、现场观察和专业研判来源，不替代专业人员判断具体处置方式。',
    unknowns: '是否存在未识别的相邻物质或二次风险；多源信息是否一致。',
    information: [
      {
        name: '物质燃烧、氧化、反应与热稳定性资料',
        purpose: '理解物质在受热或相互作用时可能出现的情况',
        acquisition: 'SDS、企业工艺资料、专业数据库',
        sourceIds: ['SRC-GBT-16483'],
      },
      {
        name: '火情、烟气与热异常记录',
        purpose: '掌握现场的连续变化，而不是一个时间点的印象',
        acquisition: '现场视频、热成像、观察记录',
        sourceIds: ['SRC-AQ-3052'],
      },
      {
        name: '相邻设备与库存信息',
        purpose: '识别可能被波及的邻近危险源',
        acquisition: '企业总平面、库存台账',
        sourceIds: ['SRC-GB-18218'],
      },
      {
        name: '工艺资料',
        purpose: '理解温度、压力等工艺条件与安全设计意图',
        acquisition: '企业工艺文件、设备说明',
        sourceIds: ['SRC-REG-591'],
      },
      {
        name: '事故时间线',
        purpose: '把各方报告按时间对齐，找出不一致之处',
        acquisition: '报警、指挥与监测记录',
        sourceIds: ['SRC-AQ-3052'],
      },
    ],
    capabilities: [
      {
        key: 'remote-recon',
        purpose: '远程观察容器、管线和设备的受热、变形与破损迹象，减少近距离接近。',
        output: '远程画面与位置线索；图像不能证明物质种类或反应状态。',
      },
      {
        key: 'fire-observation',
        purpose: '连续记录火情、烟气与热异常的变化，为专业研判提供连续观察材料。',
        output: '火情与热异常的时间序列记录；不据此选择灭火介质或冷却方式。',
      },
      {
        key: 'gas-detection',
        purpose: '在火场周边记录气体与环境参数变化，提示可能出现的新释放。',
        output: '多点、多时段的检测记录与异常提示。',
      },
      {
        key: 'command-coordination',
        purpose: '把工艺资料、库存信息、事故时间线和各方研判放在一起，暴露彼此不一致之处。',
        output: '多源信息对照，以及待专业确认的分歧点。',
      },
    ],
    sourceIds: ['SRC-REG-591', 'SRC-AQ-3052', 'SRC-GB-18218'],
    caseIds: ['CASE-TIANJIN', 'CASE-XIANGSHUI', 'CASE-ZHANGZHOU'],
  },
  {
    key: 'impact-area',
    title: '危险影响范围与周边态势不清',
    summary: '影响可能涉及厂区、相邻企业、道路、居民区和周边公共空间。',
    whyItMatters:
      '单一观测点或未经验证的扩散模型不能代表完整影响范围。平台应把观测数据、模型推测和未知事项分开显示，不自动生成警戒半径或疏散距离。',
    unknowns: '不同监测点数据是否可比；影响范围是否随天气和事故状态变化。',
    information: [
      {
        name: '气象条件记录',
        purpose: '理解影响范围可能随天气条件发生变化',
        acquisition: '属地气象部门、现场气象观测',
        sourceIds: ['SRC-AQ-3052'],
      },
      {
        name: '厂区总平面与设备布置',
        purpose: '明确厂区内的道路、装置与相邻设施关系',
        acquisition: '企业总平面图、设备资料',
        sourceIds: ['SRC-REG-591'],
      },
      {
        name: '道路、敏感点与周边人口设施',
        purpose: '了解厂区外的公共空间与人员分布',
        acquisition: '属地提供的道路、居民区与公共设施资料',
        sourceIds: ['SRC-REG-591'],
      },
      {
        name: '多位置监测记录',
        purpose: '用不同方位的观测支持对影响范围的判断',
        acquisition: '固定与移动检测、周边点位记录',
        sourceIds: ['SRC-AQ-3052'],
      },
      {
        name: '属地已发布信息',
        purpose: '以属地已发布内容作为对外的统一口径',
        acquisition: '属地政府与应急管理部门发布渠道',
        sourceIds: ['SRC-COMMAND-QA'],
      },
    ],
    capabilities: [
      {
        key: 'gas-detection',
        purpose: '在不同方向和距离的位置记录读数，形成可比较的空间分布记录。',
        output: '多点位检测记录；点位不足或缺时间戳时须标注待核实。',
      },
      {
        key: 'remote-recon',
        purpose: '观察厂区外围、道路和相邻企业的可见状况，补充地面观察的覆盖范围。',
        output: '远程观察画面与位置线索，用于后续核实。',
      },
      {
        key: 'command-coordination',
        purpose: '汇集气象记录、属地发布信息与各方观测，按来源列出而不合并为单一结论。',
        output: '按来源标注的态势摘要与待确认项；不生成警戒半径或疏散距离。',
      },
    ],
    sourceIds: ['SRC-REG-591', 'SRC-AQ-3052', 'SRC-COMMAND-QA'],
    caseIds: ['CASE-TIANJIN', 'CASE-YIWU-DRILL'],
  },
  {
    key: 'environment-impact',
    title: '环境污染与次生影响',
    summary: '泄漏物、燃烧产物、消防废水、洗消废水和雨水径流可能影响空气、水体、土壤及排水系统。',
    whyItMatters:
      '事故控制并不意味着环境影响已经结束。平台需要保留监测点位、采样时间、检测结果和责任部门来源，避免把一次采样或模型推测当成环境结论。',
    unknowns: '是否存在未监测污染路径；检测是否具有连续性和空间代表性。',
    information: [
      {
        name: '空气、水体与土壤监测点位',
        purpose: '明确哪些介质、哪些位置已被观测',
        acquisition: '生态环境部门与企业监测记录',
        sourceIds: ['SRC-REG-591'],
      },
      {
        name: '采样时间与样品编号',
        purpose: '让每条结果可以回到具体样品',
        acquisition: '采样记录、检测机构委托单',
        sourceIds: ['SRC-REG-591'],
      },
      {
        name: '检测机构与检测结果',
        purpose: '区分有资质机构的检测与现场快速判断',
        acquisition: '有资质的检测机构报告',
        sourceIds: ['SRC-REG-591'],
      },
      {
        name: '排水路径与径流资料',
        purpose: '判断消防废水与洗消废水的可能去向',
        acquisition: '厂区排水管网资料、水务部门记录',
        sourceIds: ['SRC-DRAFT-2026'],
      },
      {
        name: '污染物资料与生态环境部门评估',
        purpose: '以主管部门评估作为环境影响的结论来源',
        acquisition: '物质资料、生态环境部门评估意见',
        sourceIds: ['SRC-REG-708'],
      },
    ],
    capabilities: [
      {
        key: 'environment-sampling',
        purpose: '登记空气、水体、土壤和径流的监测点位、采样时间与检测机构。',
        output: '采样与检测记录索引；环境结论由有资质部门或机构作出。',
      },
      {
        key: 'gas-detection',
        purpose: '在事故控制和清理阶段继续记录空气读数，判断是否仍有释放迹象。',
        output: '持续检测记录与趋势提示；单次读数不等于环境安全。',
      },
      {
        key: 'command-coordination',
        purpose: '汇总生态环境、水务、企业和消防救援的监测与处置报告。',
        output: '环境相关信息汇总与责任部门来源，不替代生态环境部门评估。',
      },
    ],
    sourceIds: ['SRC-REG-591', 'SRC-DRAFT-2026', 'SRC-REG-708'],
    caseIds: ['CASE-TIANJIN', 'CASE-XIANGSHUI'],
  },
];

export const chemicalIssues: readonly ChemicalIssue[] = issues.map((issue) => ({
  ...issue,
  information: issue.information.map((row) => ({
    ...row,
    detail: chemicalInformationDetails[informationDetailKey(issue.key, row.name)],
  })),
}));

export const defaultChemicalIssue = 'chemical-identity';

export function findChemicalIssue(key: string) {
  return issues.find((issue) => issue.key === key);
}

/**
 * 与当前问题引用同一组能力研究主题的其他问题。
 *
 * 执行包的数据基线里，“泄漏源与释放状态不清”和“危险影响范围与周边态势不清”
 * 确实引用同一组三项主题。这不是可以悄悄抹平的重复：两项能力在这两个问题下
 * 写的是不同的用途。页面把重合明说出来，读者才不会以为切换问题一定会换掉卡片。
 */
export function issuesSharingCapabilitySet(issue: ChemicalIssue): readonly string[] {
  const key = issue.capabilities
    .map((row) => row.key)
    .sort()
    .join(',');
  return issues
    .filter(
      (other) =>
        other.key !== issue.key &&
        other.capabilities
          .map((row) => row.key)
          .sort()
          .join(',') === key,
    )
    .map((other) => other.title);
}

/** 七项能力研究主题中，需要按问题提取的项。 */
export function capabilityOfIssue(issue: ChemicalIssue) {
  return issue.capabilities.flatMap((context) => {
    const capability = findChemicalCapability(context.key);
    return capability ? [{ capability, context }] : [];
  });
}

/** 每项能力关联哪些典型问题。卡片与抽屉都直接读它，避免两处各写一遍。 */
export function issuesForCapability(key: string): readonly string[] {
  return issues
    .filter((issue) => issue.capabilities.some((row) => row.key === key))
    .map((issue) => issue.title);
}
